import React, { useState } from 'react';
import { 
  Button, 
  toast, 
  Card, 
  CardContent, 
  Progress,
} from '@etherisc/ui-kit';
import { 
  Upload, 
  Download, 
  FileText, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Users,
  FileSpreadsheet,
} from 'lucide-react';
import { farmerService } from '../../services/farmerService';

interface BulkUploadConfig {
  context: 'farmer' | 'quote';
  quoteId?: string;
  uploadEndpoint: string;
  validateEndpoint: (uploadId: string) => string;
  decisionEndpoint: (uploadId: string) => string;
  downloadTemplate: () => Promise<void>;
  entityName?: string; // 'Farmers' or 'Participants'
}

interface BulkUploadModalProps {
  config?: BulkUploadConfig;  // Optional, defaults to farmer context
  onUploadComplete?: (result: ValidationResults) => void;
  onError?: (error: string) => void;
}

interface ValidationError {
  row: number;
  farmerName?: string;
  field: string;
  value: unknown;
  message: string;
}

interface DuplicateRow {
  row_number: number;
  farmer_name: string;
  farmer_phone: string;
  match_criteria: string[];
  existing_farmer_id?: string;
  existing_farmer_name?: string;
  similarity_score?: number;
}

interface ValidationResults {
  valid_rows: Record<string, unknown>[];
  invalid_rows: ValidationError[];
  total_rows: number;
  valid_count: number;
  invalid_count: number;
  error_issue_count?: number; // Total number of error issues
  duplicate_rows: DuplicateRow[];
  validation_rules_applied: string[];
}

export function BulkUploadModal({ 
  config,
  onUploadComplete, 
  onError 
}: BulkUploadModalProps) {
  // Default configuration for farmer context
  const defaultConfig: BulkUploadConfig = {
    context: 'farmer',
    uploadEndpoint: '/api/farmers/bulk-upload',
    validateEndpoint: (uploadId) => `/api/farmers/bulk-upload/${uploadId}/validate`,
    decisionEndpoint: (uploadId) => `/api/farmers/bulk-upload/${uploadId}/user-decision`,
    downloadTemplate: () => farmerService.downloadBulkUploadTemplate(),
    entityName: 'Farmers',
  };

  const activeConfig = config || defaultConfig;
  
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [validationResults, setValidationResults] = useState<ValidationResults | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<'upload' | 'validate' | 'review' | 'processing' | 'complete'>('upload');

  const downloadDuplicatesReport = (results: ValidationResults) => {
    const dateStr = new Date().toISOString().split('T')[0];
    
    let html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Duplicate Records Report - ${dateStr}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; background: #f5f5f5; }
    .container { max-width: 1200px; margin: 0 auto; background: white; padding: 30px; border-radius: 8px; }
    h1 { color: #d97706; margin-bottom: 10px; }
    .summary { background: #fef3c7; padding: 15px; border-radius: 6px; margin-bottom: 20px; }
    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
    th { background: #fbbf24; color: white; padding: 12px; text-align: left; }
    td { padding: 10px; border-bottom: 1px solid #ddd; }
    tr:hover { background: #fef3c7; }
    .badge { display: inline-block; padding: 2px 8px; background: #fbbf24; color: white; border-radius: 4px; font-size: 11px; margin: 2px; }
  </style>
</head>
<body>
  <div class="container">
    <h1>Duplicate Records Report</h1>
    <div class="summary">
      <strong>Total Duplicates:</strong> ${results.duplicate_rows.length}<br>
      <strong>Generated:</strong> ${new Date().toLocaleString()}
    </div>
    <table>
      <thead>
        <tr>
          <th>Row</th>
          <th>Name</th>
          <th>Phone</th>
          <th>Match Criteria</th>
          <th>Existing Farmer</th>
        </tr>
      </thead>
      <tbody>`;

    results.duplicate_rows.forEach(dup => {
      const criteria = dup.match_criteria.map(c => `<span class="badge">${c}</span>`).join(' ');
      html += `
        <tr>
          <td>${dup.row_number}</td>
          <td>${dup.farmer_name || 'N/A'}</td>
          <td>${dup.farmer_phone || 'N/A'}</td>
          <td>${criteria}</td>
          <td>${dup.existing_farmer_name || 'Unknown'}</td>
        </tr>`;
    });

    html += `
      </tbody>
    </table>
  </div>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk-upload-duplicates-${dateStr}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadErrorReport = (results: ValidationResults) => {
    // Generate HTML content
    const timestamp = new Date().toISOString();
    const dateStr = new Date().toISOString().split('T')[0];
    
    let html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Bulk Upload Error Report - ${dateStr}</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      margin: 0;
      padding: 20px;
      background-color: #f5f5f5;
    }
    .container {
      max-width: 1200px;
      margin: 0 auto;
      background: white;
      padding: 30px;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    h1 {
      color: #dc2626;
      border-bottom: 3px solid #dc2626;
      padding-bottom: 10px;
    }
    .summary {
      background: #fef2f2;
      border-left: 4px solid #dc2626;
      padding: 15px;
      margin: 20px 0;
    }
    .summary p {
      margin: 5px 0;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 20px;
    }
    th {
      background: #1f2937;
      color: white;
      padding: 12px;
      text-align: left;
      font-weight: 600;
    }
    td {
      padding: 10px 12px;
      border-bottom: 1px solid #e5e7eb;
    }
    tr:hover {
      background: #f9fafb;
    }
    .row-number {
      font-weight: 600;
      color: #dc2626;
    }
    .field-name {
      color: #059669;
      font-family: 'Courier New', monospace;
      font-size: 0.9em;
    }
    .value {
      color: #6b7280;
      font-family: 'Courier New', monospace;
      font-size: 0.85em;
      max-width: 300px;
      word-break: break-word;
    }
    .error-message {
      color: #374151;
    }
    .empty {
      color: #9ca3af;
      font-style: italic;
    }
    .footer {
      margin-top: 30px;
      padding-top: 20px;
      border-top: 1px solid #e5e7eb;
      color: #6b7280;
      font-size: 0.875em;
    }
  </style>
</head>
<body>
  <div class="container">
    <h1>🚨 Bulk Upload Error Report</h1>
    
    <div class="summary">
      <p><strong>Total Records:</strong> ${results.total_rows}</p>
      <p><strong>Valid Records:</strong> <span style="color: #059669">${results.valid_count}</span></p>
      <p><strong>Invalid Records:</strong> <span style="color: #dc2626">${results.invalid_count}</span>${
        results.error_issue_count && results.error_issue_count > results.invalid_count 
          ? ` with <strong>${results.error_issue_count}</strong> issues` 
          : ''
      }</p>
    </div>
    
    <h2>Error Details</h2>
    <table>
      <thead>
        <tr>
          <th>Row</th>
          <th>Name</th>
          <th>Field</th>
          <th>Value</th>
          <th>Error</th>
        </tr>
      </thead>
      <tbody>
`;
    
    results.invalid_rows.forEach(error => {
      // Format field name - capitalize and add spacing for camelCase
      const fieldName = error.field
        .replace(/([A-Z])/g, ' $1')
        .replace(/^\w/, c => c.toUpperCase())
        .trim();
      
      // Format value - show "<empty>" for empty/null/undefined values
      let value = '<span class="empty">&lt;empty&gt;</span>';
      if (error.value !== undefined && error.value !== null && String(error.value).trim() !== '') {
        // If value is an object, stringify it for better readability
        let valueStr: string;
        if (typeof error.value === 'object') {
          try {
            valueStr = JSON.stringify(error.value, null, 2);
          } catch {
            valueStr = String(error.value);
          }
        } else {
          valueStr = String(error.value);
        }
        
        const escapedValue = valueStr
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .trim();
        value = escapedValue;
      }
      
      // Format error message - ensure required fields show what is required
      let errorMessage = error.message;
      if (errorMessage.toLowerCase().includes('required') && !errorMessage.includes(fieldName)) {
        if (errorMessage === 'Required' || errorMessage === 'is required') {
          errorMessage = `${fieldName} is required`;
        } else if (!errorMessage.toLowerCase().includes(fieldName.toLowerCase())) {
          errorMessage = `${fieldName}: ${errorMessage}`;
        }
      }
      const escapedMessage = errorMessage
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      
      const farmerName = error.farmerName 
        ? error.farmerName.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        : '<span class="empty">&lt;empty&gt;</span>';
      
      html += `        <tr>
          <td class="row-number">${error.row}</td>
          <td class="value">${farmerName}</td>
          <td class="field-name">${fieldName}</td>
          <td class="value">${value}</td>
          <td class="error-message">${escapedMessage}</td>
        </tr>\n`;
    });
    
    html += `      </tbody>
    </table>
    
    <div class="footer">
      <p>Generated: ${timestamp}</p>
      <p>Total Issues: ${results.error_issue_count || results.invalid_rows.length}</p>
    </div>
  </div>
</body>
</html>`;
    
    // Create and download file
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk-upload-errors-${dateStr}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadTemplate = async () => {
    try {
      await activeConfig.downloadTemplate();
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setValidationResults(null);
      setUploadId(null);
      setCurrentStep('upload');
    }
  };

  const uploadFile = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    setCurrentStep('validate');

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      
      // Upload to backend
      const uploadResponse = await fetch(activeConfig.uploadEndpoint, {
        method: 'POST',
        body: formData,
      });
      
      if (!uploadResponse.ok) {
        const uploadError = await uploadResponse.json();
        throw new Error(`Upload failed: ${uploadError.message || uploadResponse.statusText}`);
      }
      
      const uploadResult = await uploadResponse.json();
      const newUploadId = uploadResult.data.upload.id;
      setUploadId(newUploadId);
      
      // First validate the upload (required by backend)
      const validateResponse = await fetch(activeConfig.validateEndpoint(newUploadId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!validateResponse.ok) {
        const validateError = await validateResponse.json();
        throw new Error(`Validation failed: ${validateError.message || validateResponse.statusText}`);
      }
      
      const validationResult = await validateResponse.json();
      setValidationResults(validationResult.data.validationResults || validationResult.data);
      setCurrentStep('review');
      
      toast.success('File uploaded and validated successfully');
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      toast.error(errorMessage);
      onError?.(errorMessage);
      setCurrentStep('upload');
    } finally {
      setUploading(false);
    }
  };

  const handleApproval = async (decision: 'ACCEPT_VALID' | 'REJECT_ALL') => {
    if (!uploadId) {
      toast.error('No upload to process');
      return;
    }

    setUploading(true);
    setCurrentStep('processing');

    try {
      // Step 1: Handle user decision
      const approveResponse = await fetch(activeConfig.decisionEndpoint(uploadId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision }),
      });
      
      if (!approveResponse.ok) {
        const approveError = await approveResponse.json();
        throw new Error(`Decision processing failed: ${approveError.message || approveResponse.statusText}`);
      }
      
      if (decision === 'ACCEPT_VALID') {
        // Step 2: Actually process the upload (create farmers and participants)
        console.log('🔄 Processing upload after approval...');
        
        // For farmer context, call process endpoint
        // For quote context, processing happens in handleUserDecision
        if (activeConfig.context === 'farmer') {
          // Farmer context already processes in handleUserDecision
        }
        // Note: Quote bulk upload processes farmers/participants in handleUserDecision
        
        setCurrentStep('complete');
        toast.success(`${activeConfig.entityName || 'Records'} imported successfully!`);
        if (validationResults) {
          onUploadComplete?.(validationResults);
        }
      } else {
        setCurrentStep('upload');
        toast.info('Upload rejected');
      }
      
      // Reset state
      setFile(null);
      setValidationResults(null);
      setUploadId(null);
    } catch (error) {
      console.error('Approval error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Processing failed';
      toast.error(errorMessage);
      onError?.(errorMessage);
      setCurrentStep('review');
    } finally {
      setUploading(false);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setValidationResults(null);
    setUploadId(null);
    setCurrentStep('upload');
    setUploading(false);
  };

  const renderUploadStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <FileSpreadsheet className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Bulk Upload Farmers
        </h3>
        <p className="text-sm text-gray-500 mb-6">
          Upload a CSV file to import multiple farmers at once. Download the template to see the required format.
        </p>
      </div>

      <div className="flex justify-center space-x-4 mb-6">
        <Button
          variant="outline"
          onClick={downloadTemplate}
          className="flex items-center space-x-2"
        >
          <Download className="h-4 w-4" />
          <span>Download Template</span>
        </Button>
      </div>

      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
        <div className="text-center">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
            id="csv-upload"
          />
          <label
            htmlFor="csv-upload"
            className="cursor-pointer flex flex-col items-center space-y-2"
          >
            <Upload className="h-8 w-8 text-gray-400" />
            <span className="text-sm text-gray-600">
              Click to select CSV file or drag and drop
            </span>
          </label>
        </div>
      </div>

      {file && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <FileText className="h-5 w-5 text-blue-500" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{file.name}</p>
              <p className="text-xs text-gray-500">
                {(file.size / 1024).toFixed(1)} KB
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end space-x-3">
        <Button
          onClick={uploadFile}
          disabled={!file || uploading}
          className="flex items-center space-x-2"
        >
          {uploading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Processing...</span>
            </>
          ) : (
            <>
              <Upload className="h-4 w-4" />
              <span>Upload & Validate</span>
            </>
          )}
        </Button>
      </div>
    </div>
  );

  const renderValidationResults = () => {
    if (!validationResults) return null;

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Validation Results
          </h3>
          <p className="text-sm text-gray-500">
            Review the validation results before importing farmers
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">
                {validationResults.total_rows}
              </div>
              <div className="text-sm text-gray-500">Total Records</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 text-center">
              <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-green-600">
                {validationResults.valid_count}
              </div>
              <div className="text-sm text-gray-500">Valid Records</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 flex flex-col h-full">
              <div className="text-center flex-grow">
                <AlertTriangle className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                <div className="text-2xl font-bold text-yellow-600">
                  {validationResults.duplicate_rows?.length || 0}
                </div>
                <div className="text-sm text-gray-500 mb-3">Duplicate Records</div>
              </div>
              {(validationResults.duplicate_rows?.length || 0) > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadDuplicatesReport(validationResults)}
                  className="w-full flex items-center justify-center gap-2 mt-auto"
                >
                  <Download className="h-3 w-3" />
                  <span className="text-xs">Download List</span>
                </Button>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 flex flex-col h-full">
              <div className="text-center flex-grow">
                <XCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                <div className="text-2xl font-bold text-red-600">
                  {validationResults.invalid_count}
                </div>
                <div className="text-sm text-gray-500 mb-3">
                  Invalid Records
                  {validationResults.error_issue_count && validationResults.error_issue_count > validationResults.invalid_count && (
                    <div className="text-xs text-gray-400 mt-1">
                      ({validationResults.error_issue_count} issues)
                    </div>
                  )}
                </div>
              </div>
              {validationResults.invalid_count > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadErrorReport(validationResults)}
                  className="w-full flex items-center justify-center gap-2 mt-auto"
                >
                  <Download className="h-3 w-3" />
                  <span className="text-xs">Download Report</span>
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center">
          <Button type="button" variant="outline" onClick={resetUpload}>
            Start Over
          </Button>
          
          <div className="flex items-center space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleApproval('REJECT_ALL')}
              disabled={uploading}
            >
              Cancel Import
            </Button>
            
            <Button
              type="button"
              onClick={() => handleApproval('ACCEPT_VALID')}
              disabled={uploading || validationResults.valid_count === 0}
              className="flex items-center space-x-2"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Importing...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  <span>Import {validationResults.valid_count} Farmers</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderProcessingStep = () => (
    <div className="text-center space-y-6">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
      <h3 className="text-lg font-medium text-gray-900">
        Processing Import
      </h3>
      <p className="text-sm text-gray-500">
        Please wait while we import your farmers...
      </p>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="text-center space-y-6">
      <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
      <h3 className="text-lg font-medium text-gray-900">
        Import Complete!
      </h3>
      <p className="text-sm text-gray-500">
        {validationResults?.valid_count} farmers have been successfully imported.
      </p>
      <Button type="button" onClick={resetUpload}>
        Import More Farmers
      </Button>
    </div>
  );

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardContent className="p-6">
        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              {currentStep === 'upload' && 'Step 1: Upload File'}
              {currentStep === 'validate' && 'Step 2: Validating...'}
              {currentStep === 'review' && 'Step 3: Review Results'}
              {currentStep === 'processing' && 'Step 4: Processing...'}
              {currentStep === 'complete' && 'Complete!'}
            </span>
          </div>
          <Progress 
            value={
              currentStep === 'upload' ? 25 :
              currentStep === 'validate' ? 50 :
              currentStep === 'review' ? 75 :
              currentStep === 'processing' ? 90 :
              100
            } 
            className="w-full"
          />
        </div>

        {/* Step Content */}
        {currentStep === 'upload' && renderUploadStep()}
        {(currentStep === 'review') && renderValidationResults()}
        {currentStep === 'processing' && renderProcessingStep()}
        {currentStep === 'complete' && renderCompleteStep()}
      </CardContent>
    </Card>
  );
}
